import { MapCluster } from "#components/Map/MapCluster";
import { MapClusterTooltip } from "#components/Map/MapCluster/MapClusterTooltip";
import type {
  MapClusterMassDataType,
  MapClusterPointType,
  MapClusterPropertiesType,
  MapClusterTooltipStyleType,
  MapFacilityNodeDataCollectionType,
} from "#components/Map/MapCluster/types";
import { useNavigate } from "#routers/hooks";
import {
  EmissionsHomeViewFilterAreaSchemaKeys,
  type EmissionsHomeViewFilterAreaSchemaType,
} from "#src/batteries-included-components/FilterAreas/DashboardsFilterAreas/DashboardsFilterAreas";
import { useListFacilities } from "#src/components/hooks/adapters/useFacilities";
import { DEFAULT_MAP_COORDINATES } from "#src/constants";
import { useMeasurementTypes } from "#src/contexts/MeasurementTypeContext";
import { useStickyState } from "#src/hooks/useStickyState";
import { getIntervalInMonthsFormatted, hydrateDateRange } from "#utils/date";
import { animated, useSpring } from "@react-spring/web";
import { StorageKeys } from "@validereinc/common-components";
import { CalculatorResultsDomain } from "@validereinc/domain";
import { Measurements } from "@validereinc/domain-controllers";
import { formatNumberValueAndUnit } from "@validereinc/utilities";
import classNames from "classnames/bind";
import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./EmissionsHomeMapPanel.module.scss";
import { getCachedDataUrlParams, setUrlCacheAfterLoad } from "./helpers";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";

const { PollutantsEmitted } = Measurements.logic;
const cx = classNames.bind(styles);

export const EmissionsHomeMapPanel = ({
  viewConfigStorageKey,
}: Pick<StorageKeys, "viewConfigStorageKey">) => {
  const navigate = useNavigate();
  const { getUnitByType, getPrecisionByType, getUnitName } =
    useMeasurementTypes();
  const [canReadCalculatorResults] = useIsFeatureAvailable({
    featureFlagQuery: "core:home",
    permissionQuery: "calculator_results:read",
  });
  const [canReadFacilities] = useIsFeatureAvailable({
    featureFlagQuery: "core:home",
    permissionQuery: "facilities:read",
  });

  const facilities =
    useListFacilities({}, { enabled: canReadFacilities }).data?.data ?? [];
  const [animateStyle, animate] = useSpring(() => ({ opacity: 0 }));
  const [isMapContainerReady, setMapContainerReady] = useState(false);
  const mapContainerRef = useRef(null);
  const [viewFilters] = useStickyState<EmissionsHomeViewFilterAreaSchemaType>(
    {},
    viewConfigStorageKey
  );

  const {
    [EmissionsHomeViewFilterAreaSchemaKeys.date_range]: dateRange,
    [EmissionsHomeViewFilterAreaSchemaKeys.reporting_scenario]: reportingGroup,
  } = viewFilters;
  /** https://validere.atlassian.net/browse/FE-2299 - Existing bug that is beyond the scope of this refactor */
  const { mass, label } = PollutantsEmitted.co2_eq;
  const precision = getPrecisionByType(mass);
  const unit = getUnitName(getUnitByType(mass));

  const [isUnclusteredNodesVisible, setUnclusteredNodesVisible] =
    useState<boolean>(true);

  const getAvgFacilityLocation = () => {
    if (facilities.length > 0) {
      const totalCoordinates = facilities.reduce(
        (totals, facility) => ({
          latitude: totals.latitude + (facility?.latitude ?? 0),
          longitude: totals.longitude + (facility?.longitude ?? 0),
        }),
        { latitude: 0, longitude: 0 }
      );
      const facilityCount = facilities.reduce((count, facility) => {
        return facility.latitude ? count + 1 : count;
      }, 0);

      const latitude = totalCoordinates.latitude / facilityCount;
      const longitude = totalCoordinates.longitude / facilityCount;

      //to handle demo accouts - if the avg location is in the atlantic ocean, default to US
      if (longitude > -74) {
        return DEFAULT_MAP_COORDINATES;
      } else {
        return {
          latitude,
          longitude,
        };
      }
    }
    return DEFAULT_MAP_COORDINATES;
  };

  const initialViewState = {
    ...getAvgFacilityLocation(),
    zoom: 3.5,
    projection: "mercator",
  };

  const [tooltipStyle, setTooltipStyle] =
    useState<MapClusterTooltipStyleType>(null);
  const [tooltipData, setTooltipData] = useState<MapClusterMassDataType>(null);

  // reduce the facilities so that all latitude and longitude pairs are unique
  // remove duplicates which is a problem on staging with poor data but not on prod with clean data
  const getUniqueFacilities = (): MapFacilityNodeDataCollectionType => {
    const uniqueFacilities: MapFacilityNodeDataCollectionType =
      facilities && facilities.length > 0
        ? facilities.reduce((acc: any, facility: any) => {
            const facilityExists = acc.find(
              (accFacility) =>
                accFacility.latitude === facility.latitude &&
                accFacility.longitude === facility.longitude
            );
            if (
              !facilityExists &&
              facility.latitude !== null &&
              facility.longitude !== null
            ) {
              acc.push(facility);
            }
            return acc;
          }, [])
        : [];

    return uniqueFacilities;
  };

  const filteredFacilities = useMemo(() => getUniqueFacilities(), [facilities]);

  /**
   * getFacilityEmissionsData will fetch the emissions data for a single facility on hover of a map node
   */
  const getFacilityEmissionsData = async (
    id: string,
    reportingGroup: string,
    mass: string
  ): Promise<number> => {
    if (!canReadCalculatorResults) {
      return 0;
    }
    const request = {
      page: 1,
      pageSize: 1,
      filters: {
        "equipment.facility.id": id,
        year_month: {
          // @ts-expect-error TODO: date format is not correct
          $in: getIntervalInMonthsFormatted(hydrateDateRange(dateRange)),
        },
        reporting_group: reportingGroup,
        isAlreadyFormatted: true,
      },

      group_by: ["equipment.facility.id", "equipment.facility.name"],
    };

    const response = await CalculatorResultsDomain.getList(request as any);
    const item =
      response.data && response.data.length > 0 ? response.data[0] : null;

    const measurement = Number(item?.measurement[mass]) || 0;

    setUrlCacheAfterLoad(id, measurement);

    return measurement;
  };

  // give the map time to draw to ensure that the map container is ready and then fade in the map
  useEffect(() => {
    setTimeout(() => {
      setMapContainerReady(true);
      animate({ opacity: 1, delay: 500 });
    }, 1000);
    // }
  }, []);

  const mouseEnterCallback = async (
    properties: MapClusterPropertiesType,
    point: MapClusterPointType
  ) => {
    setUnclusteredNodesVisible(false);
    setTooltipStyle({ top: point.y - 1, left: point.x - 8, opacity: 1 });
    const dataWithoutMass = {
      ...properties,
      label,
    };

    const { cachedFacilityId, cachedFacilityMeasurement } =
      getCachedDataUrlParams();

    if (cachedFacilityId === properties.id) {
      const cachedDataWithMass = {
        ...dataWithoutMass,
        mass:
          formatNumberValueAndUnit(
            { value: parseFloat(cachedFacilityMeasurement ?? ""), unit },
            { overrideOpts: { minimumFractionDigits: precision ?? 0 } }
          ) ?? "-",
      };
      return setTooltipData(cachedDataWithMass as any);
    }

    const dataWithMass = await getPointData(dataWithoutMass);
    // once we have the mass, update the tooltip data to include it removing the skeleton loader
    setTooltipData(dataWithMass as any);
  };

  const handleClosePopup = () => {
    setTooltipStyle({ ...tooltipStyle, opacity: 0 } as any);
    setTimeout(() => {
      // reset the point
      setUnclusteredNodesVisible(true);
      setTooltipData(null);
    }, 500);
  };

  const handleSetPopupData = (data: MapClusterMassDataType) => {
    setTooltipStyle(data as any);
  };

  const getPointData = async (properties: MapClusterPropertiesType) => {
    const measurement = await getFacilityEmissionsData(
      properties?.id,
      reportingGroup ?? "",
      mass
    );

    const facilityData = {
      id: properties?.id,
      name: properties?.name ?? "No facility nearby",
      description: properties?.industry_segment?.name ?? "",
      url: `/app/organization/facilities/${properties?.id}/detail`,
      label,
      mass:
        formatNumberValueAndUnit(
          { value: measurement, unit },
          { overrideOpts: { minimumFractionDigits: precision ?? 0 } }
        ) ?? "-",
    };
    return facilityData;
  };

  const isFacilitiesAvailable =
    !canReadFacilities || filteredFacilities.length > 0;

  if (!isFacilitiesAvailable) {
    return (
      <div
        style={{ height: "100%", width: "100%", position: "relative" }}
        ref={mapContainerRef}
      >
        <div className={cx("mapContainer")} />
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          minHeight: 480,
          height: "100%",
          width: "100%",
          position: "relative",
        }}
        ref={mapContainerRef}
      >
        <MapClusterTooltip
          data={tooltipData as any}
          handleClosePopup={handleClosePopup}
          tooltipStyle={tooltipStyle as any}
          navigate={navigate as any}
        />

        <div className={cx("mapContainer")}>
          {isMapContainerReady && isFacilitiesAvailable ? (
            <animated.div
              style={{
                ...animateStyle,
                height: "100%",
                width: "100%",
                padding: 0,
                margin: 0,
                overflow: "hidden",
              }}
            >
              <MapCluster
                initialViewState={initialViewState}
                data={filteredFacilities}
                onMapItemClick={handleSetPopupData as any}
                mouseEnterCallback={mouseEnterCallback as any}
                isUnclusteredNodesVisible={isUnclusteredNodesVisible}
              />
            </animated.div>
          ) : null}
        </div>
      </div>
    </>
  );
};
